import React from 'react';
import {
  Card,
  Typography,
  Button,
  Grid,
  Box,
  Chip
} from '@mui/material';
import {
  Check,
  DirectionsCar,
  ConfirmationNumber
} from '@mui/icons-material';

const professionalPlansData = [
  {
    id: "pro-6",
    name: "Motorista 6 Lavagens",
    price: {
      montly: 179.10,
      annual: 1719.36
    },
    description: "6 lavagens mensais",
    features: [
      "6 lavagens por mês",
      "Lavagem simples (externa + interna básica)",
      "Atendimento em lava-rápidos selecionados",
      "Ideal para uso profissional recorrente",
      "Sem permanência mínima",
      "Cancelamento a qualquer momento"
    ],
    badges: ["Uber", "99", "Táxi"],
    icon: DirectionsCar,
    popular: false,
    color: "#06b6d4"
  },
  {
    id: "pro-10",
    name: "Motorista 10 Lavagens",
    price: {
      montly: 206.10,
      annual: 1978.56
    },
    description: "10 lavagens mensais",
    features: [
      "10 lavagens por mês",
      "Lavagem simples (externa + interna básica)",
      "Atendimento em lava-rápidos selecionados",
      "Ideal para uso profissional recorrente",
      "Sem permanência mínima",
      "Cancelamento a qualquer momento"
    ],
    badges: ["Uber", "99", "Táxi"],
    icon: DirectionsCar,
    popular: true,
    color: "#06b6d4"
  }
];

interface ProfessionalPlansPricingSectionProps {
  onOpenCouponModal: () => void
}

export const ProfessionalPlansPricingSection = ({ onOpenCouponModal }: ProfessionalPlansPricingSectionProps) => {
  // Format price to Brazilian currency format
  const formatPrice = (price: number) => {
    return price.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  return (
    <Grid container spacing={3} sx={{ mb: 4, justifyContent: 'center' }}>
      {professionalPlansData.map((plan) => {
        const Icon = plan.icon;

        const normalPriceAnnually = plan.price.montly * 12
        const savings = normalPriceAnnually - plan.price.annual

        return (
          <Grid item xs={12} md={6} lg={5} key={plan.id} sx={{ display: 'flex' }}>
            <Card
              sx={{
                position: 'relative',
                p: 3,
                boxShadow: plan.popular
                  ? '0 8px 30px rgba(6, 182, 212, 0.15)'
                  : '0 4px 20px rgba(30, 58, 138, 0.1)',
                background: plan.popular
                  ? 'linear-gradient(145deg, rgba(6, 182, 212, 0.05) 0%, transparent 100%)'
                  : 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                border: plan.popular ? '2px solid #06b6d4' : '1px solid #e2e8f0',
                borderRadius: '10px',
                transform: plan.popular ? 'scale(1.05)' : 'scale(1)',
                transition: 'all 0.3s ease',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'visible',
                '&:hover': {
                  boxShadow: '0 8px 30px rgba(30, 58, 138, 0.15)',
                  transform: plan.popular ? 'scale(1.05) translateY(-8px)' : 'translateY(-8px)'
                }
              }}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <Chip
                  label="Mais Popular"
                  sx={{
                    position: 'absolute',
                    top: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#06b6d4',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    zIndex: 10
                  }}
                />
              )}

              <Box sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', pt: 2 }}>
                {/* Icon */}
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    background: plan.color,
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.1)'
                    }
                  }}
                >
                  <Icon sx={{ color: 'white', width: 32, height: 32 }} />
                </Box>

                {/* Plan Name */}
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#1a202c',
                    mb: 1
                  }}
                >
                  {plan.name}
                </Typography>

                {/* Badges (Uber, 99, Táxi) */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  {plan.badges.map((badge) => (
                    <Chip
                      key={badge}
                      label={badge}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(6, 182, 212, 0.1)',
                        color: '#06b6d4',
                        fontWeight: 600,
                        fontSize: '0.7rem'
                      }}
                    />
                  ))}
                </Box>

                {/* Price */}
                <Box sx={{ mb: 2 }}>
                  {/* Mensal */}
                  <Typography
                    sx={{
                      fontSize: '0.85rem',
                      color: '#64748b',
                      mb: 0.5
                    }}
                  >
                    Plano mensal
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: '1.4rem',
                      fontWeight: 400,
                      color: '#1a202c'
                    }}
                  >
                    R$ {formatPrice(plan.price.montly)}<span style={{ fontSize: '0.9rem', fontWeight: 400 }}> /mês</span>
                  </Typography>

                  {/* Divider */}
                  <Box sx={{
                    my: 2,
                    borderTop: '1px dashed #e2e8f0',
                    position: 'relative'
                  }}>
                    <Chip
                      label="OU ANUAL"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -10,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: '#64748b', //'#f1f5f9',
                        color: 'white',
                        fontSize: '0.7rem'
                      }}
                    />
                  </Box>

                  {/* Anual destaque */}
                  <Box sx={{
                    background: 'linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)',
                    border: '1px solid #67e8f9',
                    borderRadius: 2,
                    px: '7px',
                    py: 2.5,
                    marginTop: '28px'
                  }}>
                    <Typography
                      sx={{
                        fontSize: '1.6rem',
                        fontWeight: 800,
                        color: '#06b6d4',
                        lineHeight: 1.2
                      }}
                    >
                      R$ {formatPrice(plan.price.annual / 12)}<span style={{ fontSize: '0.9rem' }}> /mês*</span>
                    </Typography>

                    {/* Economia */}
                    <Typography
                      sx={{
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: '#16a34a',
                        mt: 0.5
                      }}
                    >
                      Economize R$ {formatPrice(savings)}
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          color: '#16a34a',
                          mt: 0.5
                        }}>
                        (20% OFF)
                      </Typography>
                    </Typography>
                  </Box>
                </Box>

                {/* Description */}
                <Typography
                  variant="body2"
                  sx={{
                    color: '#64748b',
                    mb: 3,
                    fontWeight: 600
                  }}
                >
                  {plan.description}
                </Typography>

                {/* Features */}
                <Box sx={{ mb: 3, flex: 1, textAlign: 'left' }}>
                  {plan.features.map((feature, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        mb: 1.5
                      }}
                    >
                      <Check sx={{
                        color: '#06b6d4',
                        width: 20,
                        height: 20,
                        mr: 1,
                        flexShrink: 0,
                        mt: 0.25
                      }} />
                      <Typography
                        variant="body2"
                        sx={{ color: '#1a202c', lineHeight: 1.6 }}
                      >
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* CTA Button */}
                <Button
                  fullWidth
                  variant={plan.popular ? "contained" : "outlined"}
                  startIcon={<ConfirmationNumber />}
                  onClick={onOpenCouponModal}
                  sx={{
                    fontWeight: 600,
                    backgroundColor: plan.popular ? '#06b6d4' : 'transparent',
                    color: plan.popular ? 'white' : '#06b6d4',
                    borderColor: '#06b6d4',
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: plan.popular ? '#0891b2' : '#06b6d4',
                      color: 'white'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Resgatar Cupom
                </Button>
              </Box>
            </Card>
          </Grid>
        );
      })}

      {/* Footer Note */}
      <Box sx={{ textAlign: 'center', mt: 6 }}>
        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          <span style={{ fontWeight: 700 }}>* Valor cobrado no plano anual com parcela única.</span>
        </Typography>
      </Box>
    </Grid>
  )
}